import axios from 'axios';
import { API_URL } from '../config';
import type {
    StatsRequest,
    StatsResponse,
    ConnectionStats,
    MediaImportStats,
    GameCreationStats,
    MediaConsultationStats,
    GamePlayStats,
    PerformanceStats,
    GameDurationStats,
    GameSatisfactionStats,
    ScoringStats
} from '../models/statistics';

const url = API_URL + '/stats';

// ============================================================================
// Session Management
// ============================================================================

/**
 * Starts a new session for the user
 */
export const startSession = async (token: string): Promise<any> => {
    try {
        const response = await axios.post(`${url}/session/start`, {}, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error starting session:', error);
        return null;
    }
};

/**
 * Retrieves scoring statistics (Scoring Stimul'in)
 * Calculates score based on formula: Score = xA - yB - zC
 * Where A = level, B = clue count, C = error count
 */
export const getScoringStats = async (
    token: string,
    request: StatsRequest
): Promise<ScoringStats | null> => {
    try {
        const response = await axios.post<{
            data: {
                labels: string[];
                scores: number[];
                gameCounts: number[];
            };
            evolution?: number;
        }>(
            `${url}/game/scoring`,
            { ...request, type: request.type || 'ALL' },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        );

        return {
            labels: response.data.data.labels || [],
            scores: response.data.data.scores || [],
            gameCounts: response.data.data.gameCounts || [],
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error('Error fetching scoring stats:', error);
        return null;
    }
};

/**
 * Retrieves satisfaction rate and vote counts per game type
 */
export const getGameSatisfactionStats = async (
    token: string,
    request: StatsRequest
): Promise<GameSatisfactionStats | null> => {
    try {
        const response = await axios.post<{
            data: {
                labels: string[];
                satisfactionRate: number[];
                totalVotes: number[];
                noOpinionVotes: number[];
                totalVotesAll?: number;
                noOpinionVotesAll?: number;
                globalAverageRating?: number;
                globalSatisfactionPercent?: number;
            }
        }>(
            `${url}/game/satisfaction-by-type`,
            { ...request },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        );

        return {
            labels: response.data.data.labels || [],
            satisfactionRate: response.data.data.satisfactionRate || [],
            totalVotes: response.data.data.totalVotes || [],
            noOpinionVotes: response.data.data.noOpinionVotes || [],
            totalVotesAll: response.data.data.totalVotesAll,
            noOpinionVotesAll: response.data.data.noOpinionVotesAll,
            globalAverageRating: response.data.data.globalAverageRating,
            globalSatisfactionPercent: response.data.data.globalSatisfactionPercent
        };
    } catch (error) {
        console.error('Error fetching game satisfaction stats:', error);
        return null;
    }
};

/**
 * Ends a session for the user
 */
export const endSession = async (
    token: string,
    sessionId: string,
    duration: number,
    useKeepalive = false
): Promise<any> => {
    try {
        // If useKeepalive is true, use fetch with keepalive to ensure sending even when the page is unloading.
        if (useKeepalive) {
            const response = await fetch(`${url}/session/end`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ sessionId, duration }),
                keepalive: true // Ensures the request will complete even if the page is closing
            });

            if (!response.ok) {
                console.error('Failed to end session:', response.status);
                return null;
            }
            return await response.json();
        }

        const response = await axios.post(`${url}/session/end`, {
            sessionId,
            duration
        }, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error ending session:', error);
        return null;
    }
};

/**
 * Starts a view activity (route/area presence)
 */
export const startView = async (
    token: string,
    payload: { action?: string; details: any }
): Promise<any> => {
    try {
        const response = await axios.post(`${url}/activity/start`, payload, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error starting view activity:', error);
        return null;
    }
};

/**
 * Ends a view activity (route/area presence)
 */
export const endView = async (
    token: string,
    payload: { action?: string; details: any },
    useKeepalive = false
): Promise<any> => {
    try {
        // If keepalive requested, prefer navigator.sendBeacon then fallback to fetch
        if (useKeepalive) {
            const endpoint = `${url}/activity/end`;
            // Try fetch with Authorization header first
            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload),
                    keepalive: true
                });
                if (response.ok) {
                    return await response.json();
                }
                console.warn('Keepalive fetch returned non-ok, will try sendBeacon fallback', response.status);
            } catch (e) {
                console.warn('Keepalive fetch failed, will try sendBeacon fallback', e);
            }

            // Fallback to sendBeacon with token in payload (beacon cannot set headers)
            if (typeof navigator !== 'undefined' && (navigator as any).sendBeacon) {
                try {
                    const beaconPayload = { ...payload, authToken: token };
                    const blob = new Blob([JSON.stringify(beaconPayload)], { type: 'application/json' });
                    const ok = (navigator as any).sendBeacon(endpoint, blob);
                    return { ok: !!ok, beacon: true };
                } catch (e) {
                    console.warn('sendBeacon failed', e);
                }
            }

            // Last resort: non-keepalive fetch
            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
                if (!response.ok) {
                    console.error('Failed to end view activity (no-keepalive):', response.status);
                    return null;
                }
                return await response.json();
            } catch (e) {
                console.error('Failed to end view activity (final fallback):', e);
                return null;
            }
        }

        const response = await axios.post(`${url}/activity/end`, payload, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error ending view activity:', error);
        return null;
    }
};

// ============================================================================
// Game Statistics
// ============================================================================

/**
 * Records when a game is started
 */
export const startGame = async (
    token: string,
    gameId: string,
    gameType?: string
): Promise<any> => {
    try {
        const payload: any = {
            game_id: gameId,
            timestamp: new Date().toISOString()
        };

        if (gameType) {
            payload.game_type = gameType;
        }

        const response = await axios.post(`${url}/game-start/${gameId}`, payload, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error recording game start:', error);
        return null;
    }
};

/**
 * Inserts game completion statistics
 */
export const insertStats = async (
    token: string,
    data: any
): Promise<any> => {
    try {
        const gameId = data.idGame;
        const payload = {
            duration: data.timeTakenToCompleteGame,
            level: data.difficultyLevel,
            errorCount: data.errorCount,
            clueCount: data.clueCount
        };
        const response = await axios.post(`${url}/game-completion/${gameId}`, payload, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error inserting game stats:', error);
        return null;
    }
};

/**
 * Inserts user satisfaction for a finished game
 */
export const insertSatisfaction = async (
    token: string,
    gameId: string,
    rating: number,
    gameType?: string,
): Promise<any> => {
    try {
        const payload: any = { rating };
        if (gameType) payload.game_type = gameType;

        const response = await axios.post(`${url}/game-satisfaction/${gameId}`, payload, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error inserting game satisfaction:', error);
        return null;
    }
};

/**
 * Retrieves game play statistics
 */
export const getGamePlayStats = async (
    token: string,
    request: StatsRequest
): Promise<GamePlayStats | null> => {
    try {
        const response = await axios.post<{ data: { start: number[]; finish: number[]; totalStarted?: number; totalFinished?: number }; evolution?: number }>(
            `${url}/game/play`,
            { ...request, type: request.type || 'ALL' },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        );

        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);

        return {
            labels,
            started: response.data.data.start || [],
            finished: response.data.data.finish || [],
            totalStarted: response.data.data.totalStarted,
            totalFinished: response.data.data.totalFinished,
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error('Error fetching game play stats:', error);
        return null;
    }
};

/**
 * Retrieves performance statistics (difficulty, errors, clues)
 */
export const getPerformanceStats = async (
    token: string,
    request: StatsRequest
): Promise<PerformanceStats | null> => {
    try {
        // Retrieve the 3 metrics in parallel
        const [levelResponse, errorResponse, clueResponse] = await Promise.all([
            axios.post<{ data: { totalLevel: number; averageLevel?: number; medianLevel?: number; level: number[] }; evolution?: number }>(
                `${url}/game/level`,
                { ...request, type: request.type || 'ALL' },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            ),
            axios.post<{ data: { totalError: number; error: number[] } }>(
                `${url}/game/error`,
                { ...request, type: request.type || 'ALL' },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            ),
            axios.post<{ data: { totalClue: number; clue: number[] } }>(
                `${url}/game/clue`,
                { ...request, type: request.type || 'ALL' },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )
        ]);

        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);

        return {
            labels,
            difficulty: levelResponse.data.data.level || [],
            averageLevel: levelResponse.data.data.averageLevel,
            medianDifficulty: levelResponse.data.data.medianLevel,
            errors: errorResponse.data.data.error || [],
            clues: clueResponse.data.data.clue || [],
            evolution: levelResponse.data.evolution
        };
    } catch (error) {
        console.error('Error fetching performance stats:', error);
        return null;
    }
};

/**
 * Retrieves game duration statistics (average level and duration)
 */
export const getGameDurationStats = async (
    token: string,
    request: StatsRequest
): Promise<GameDurationStats | null> => {
    try {
        // Retrieve level and duration in parallel
        const [levelResponse, durationResponse] = await Promise.all([
            axios.post<{ data: { totalLevel: number; medianLevel?: number; level: number[] } }>(
                `${url}/game/level`,
                { ...request, type: request.type || 'ALL' },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            ),
            axios.post<{ data: { totalDuration: number; totalMinutes?: number; median?: number; medianSeconds?: number; duration: number[]; durationSeconds?: number[] }; evolution?: number }>(
                `${url}/game/duration`,
                { ...request, type: request.type || 'ALL' },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )
        ]);

        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);

        return {
            labels,
            averageLevel: levelResponse.data.data.level || [],
            medianLevel: levelResponse.data.data.medianLevel,
            medianDuration: durationResponse.data.data.duration || [],
            medianDurationSeconds: durationResponse.data.data.durationSeconds,
            median: durationResponse.data.data.median,
            medianSeconds: durationResponse.data.data.medianSeconds,
            totalDuration: durationResponse.data.data.totalDuration,
            totalMinutes: durationResponse.data.data.totalMinutes,
            evolution: durationResponse.data.evolution
        };
    } catch (error) {
        console.error('Error fetching game duration stats:', error);
        return null;
    }
};

// ============================================================================
// Media Statistics
// ============================================================================

/**
 * Records media lookup/watch statistics
 */
export const lookupMedia = async (
    token: string,
    mediaId: string,
    duration: number
): Promise<any> => {
    try {
        const response = await axios.post(`${url}/media-watch/${mediaId}`, {
            duration: duration
        }, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error recording media lookup:', error);
        return null;
    }
};

// ============================================================================
// Connection Statistics
// ============================================================================

/**
 * Retrieves activity time statistics (in minutes)
 */
export const getActivityTimeStats = async (
    token: string,
    request: StatsRequest
): Promise<ConnectionStats | null> => {
    try {
        const response = await axios.post<{ data: number[]; median?: number; totalMinutes?: number; evolution?: number }>(
            `${url}/activity-time`,
            request,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        );

        // Generate labels based on the period
        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);

        return {
            labels,
            data: response.data.data || [],
            median: response.data.median,
            totalMinutes: response.data.totalMinutes,
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error('Error fetching activity time stats:', error);
        return null;
    }
};

const getActionStats = async (
    token: string,
    request: StatsRequest,
    action: string
): Promise<{ labels: string[]; data: number[]; total?: number; evolution?: number } | null> => {
    try {
        const response = await axios.post<{ data: number[]; total?: number; evolution?: number }>(
            `${url}/activity/action`,
            { ...request, action },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        );

        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);

        return {
            labels,
            data: response.data.data || [],
            total: response.data.total,
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error(`Error fetching action stats for ${action}:`, error);
        return null;
    }
};

export const getGameCreationStats = (
    token: string,
    request: StatsRequest
): Promise<GameCreationStats | null> => {
    return getActionStats(token, request, 'created_game');
};

export const getMediaCreationStats = (
    token: string,
    request: StatsRequest
): Promise<MediaImportStats | null> => {
    return getActionStats(token, request, 'created_media');
};

export const getMediaConsultationStats = async (
    token: string,
    request: StatsRequest
): Promise<MediaConsultationStats | null> => {
    try {
        const response = await axios.post<{ data: { totalDuration: number; duration: number[] }; evolution?: number }>(
            `${url}/media/consultation`,
            request,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        );

        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);

        return {
            labels,
            duration: response.data.data.duration || [],
            totalDuration: response.data.data.totalDuration,
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error('Error fetching media consultation stats:', error);
        return null;
    }
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generates a normalized period key for a date (uses UTC to match backend behavior)
 */
const getPeriodKey = (date: Date, period: string): string => {
    const normalized = new Date(date);

    switch (period) {
        case 'day':
            normalized.setUTCHours(0, 0, 0, 0);
            return formatDate(normalized, 'DD/MM', true);
        case 'week':
            // Normalize to Monday of the week (UTC)
            const day = normalized.getUTCDay();
            const diff = normalized.getUTCDate() - day + (day === 0 ? -6 : 1);
            normalized.setUTCDate(diff);
            normalized.setUTCHours(0, 0, 0, 0);
            return formatDate(normalized, 'DD/MM', true);
        case 'month':
            normalized.setUTCDate(1);
            normalized.setUTCHours(0, 0, 0, 0);
            return formatDate(normalized, 'MM/YYYY', true);
        case 'year':
            normalized.setUTCMonth(0, 1);
            normalized.setUTCHours(0, 0, 0, 0);
            return formatDate(normalized, 'YYYY', true);
        default:
            return date.toISOString();
    }
};

/**
 * Generates date labels based on the period
 * Uses the same logic as backend to ensure proper alignment (UTC normalization)
 */
const generateLabels = (startDate: string, endDate: string, period: string): string[] => {
    const labels: string[] = [];
    const labelsSet = new Set<string>();
    const start = new Date(startDate);
    const end = new Date(endDate);
    const current = new Date(start);

    current.setUTCHours(0, 0, 0, 0);
    end.setUTCHours(23, 59, 59, 999);

    // Generate keys for each day in the range, then normalize them
    while (current <= end) {
        const key = getPeriodKey(current, period);
        if (!labelsSet.has(key)) {
            labelsSet.add(key);
            labels.push(key);
        }
        current.setUTCDate(current.getUTCDate() + 1);
    }

    return labels;
};

/**
 * Formats a date according to the specified format
 */
const formatDate = (date: Date, format: string, useUTC = false): string => {
    const day = String(useUTC ? date.getUTCDate() : date.getDate()).padStart(2, '0');
    const month = String((useUTC ? date.getUTCMonth() : date.getMonth()) + 1).padStart(2, '0');
    const year = useUTC ? date.getUTCFullYear() : date.getFullYear();

    return format
        .replace('DD', day)
        .replace('MM', month)
        .replace('YYYY', String(year));
};

/**
 * Date presets for filters
 */
export const getDatePresets = () => {
    const now = new Date();

    return [
        {
            label: '7 derniers jours',
            startDate: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
            endDate: now
        },
        {
            label: '30 derniers jours',
            startDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
            endDate: now
        },
        {
            label: '3 derniers mois',
            startDate: new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
            endDate: now
        },
        {
            label: 'Cette année',
            startDate: new Date(now.getFullYear(), 0, 1),
            endDate: now
        },
        {
            label: 'Année dernière',
            startDate: new Date(now.getFullYear() - 1, 0, 1),
            endDate: new Date(now.getFullYear() - 1, 11, 31)
        }
    ];
};
